/**
 * Application-wide constants — must stay in sync with server/utils/constants.js
 */

// ── Reservation defaults ─────────────────────────────────────
/** Must match DEFAULT_RESERVATION_LIMIT in server/utils/clientReservationLimit.js */
export const DEFAULT_RESERVATION_LIMIT = 5000;
export const DEFAULT_CURRENCY = 'USD';
export const DEFAULT_CURRENCY_SYMBOL = '$';

export const SUPPORTED_CURRENCIES = Object.freeze([
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
]);

export const SUPPORTED_CURRENCY_CODES = Object.freeze(SUPPORTED_CURRENCIES.map((c) => c.code));

// ── Order Statuses ──────────────────────────────────────────
export const ORDER_STATUSES = Object.freeze({
  REQUESTED: 'requested',
  APPROVED: 'approved',
  READY: 'ready',
  MEMO: 'memo',
  PAID: 'paid',
  CANCELED: 'canceled',
  EXPIRED: 'expired',
});

export const ALL_ORDER_STATUSES = Object.freeze(Object.values(ORDER_STATUSES));


// ── Diamond (Order) Types ───────────────────────────────────
export const DIAMOND_TYPES = Object.freeze({
  CERTIFIED: 'certified',
  NON_CERTIFIED: 'nonCertified',
  CUSTOM: 'custom',
});

export const ALL_DIAMOND_TYPES = Object.freeze(Object.values(DIAMOND_TYPES));

// ── Non-certified lot order modes ───────────────────────────
export const ORDER_MODES = Object.freeze({
  BY_WEIGHT: 'byWeight',
  BY_COUNT: 'byCount',
});

export const ALL_ORDER_MODES = Object.freeze(Object.values(ORDER_MODES));

// ── Diamond shapes ──────────────────────────────────────────
/** Must stay in sync with server/utils/diamondConstants.js DIAMOND_SHAPE_LIST */
/** Must stay in sync with server/utils/diamondConstants.js */
export const COLOR_CATEGORIES = Object.freeze({
  STANDARD: 'standard',
  FANCY: 'fancy',
});

export const FANCY_COLOR_INTENSITY_LIST = Object.freeze([
  'Faint',
  'Very Light',
  'Light',
  'Fancy Light',
  'Fancy',
  'Fancy Intense',
  'Fancy Vivid',
  'Fancy Deep',
  'Fancy Dark',
]);

export const FANCY_COLOR_HUE_LIST = Object.freeze([
  'Yellow',
  'Orange',
  'Pink',
  'Red',
  'Purple',
  'Violet',
  'Blue',
  'Green',
  'Brown',
  'Gray',
  'Black',
  'White',
  'Champagne',
  'Cognac',
  'Chameleon',
]);

export const FANCY_COLOR_OVERTONE_LIST = Object.freeze([
  'Brownish',
  'Grayish',
  'Greenish',
  'Bluish',
  'Orangy',
  'Pinkish',
  'Purplish',
  'Reddish',
  'Violetish',
  'Yellowish',
]);

/** GIA-style fancy color label, e.g. "Fancy Vivid Yellowish Green" */
export function formatFancyColor(fancyColor) {
  if (!fancyColor || typeof fancyColor !== 'object') return '—';
  const { intensity, hue, overtone } = fancyColor;
  if (!intensity || !hue) return '—';
  const parts = [intensity];
  if (overtone && String(overtone).trim()) parts.push(String(overtone).trim());
  parts.push(hue);
  return parts.join(' ');
}

export const DIAMOND_SHAPE_LIST = Object.freeze([
  'ROUND',
  'PRINCESS',
  'CUSHION',
  'CUSHION EL',
  'CUSHION SQ',
  'OVAL',
  'EMERALD',
  'RADIANT',
  'PEAR',
  'ASSCHER',
  'MARQUISE',
  'HEART',
  'TRIANGLE',
  'TRILLIANT',
  'BAGUETTE',
]);

// ── Pricing Rule Types ──────────────────────────────────────
export const PRICING_RULE_TYPES = Object.freeze({
  NONE: 'none',
  FIXED_PER_CARAT: 'fixed_per_carat',
  PERCENTAGE: 'percentage',
});

export const ALL_PRICING_RULE_TYPES = Object.freeze(Object.values(PRICING_RULE_TYPES));

export const SPECIAL_REQUEST_CATEGORIES = Object.freeze({
  CERTIFIED: 'certified',
  NON_CERTIFIED: 'nonCertified',
  OTHER: 'other',
});

export const ALL_SPECIAL_REQUEST_CATEGORIES = Object.freeze(Object.values(SPECIAL_REQUEST_CATEGORIES));

export const SPECIAL_REQUEST_STATUSES = Object.freeze({
  SUBMITTED: 'submitted',
  IN_PROGRESS: 'inProgress',
  READY: 'ready',
  DENIED: 'denied',
  FULFILLED: 'fulfilled',
});

export const ALL_SPECIAL_REQUEST_STATUSES = Object.freeze(Object.values(SPECIAL_REQUEST_STATUSES));

// ── Order Group Statuses ────────────────────────────────────
export const ORDER_GROUP_STATUSES = Object.freeze({
  OPEN: 'open',
  READY: 'ready',
  MEMO: 'memo',
  PAID: 'paid',
  CANCELLED: 'cancelled',
});
// ── Payment Methods ─────────────────────────────────────────
export const PAYMENT_METHODS = Object.freeze({
  CREDIT_CARD: 'creditCard',
  CASH: 'cash',
  CHECK: 'check',
  APPLICATION: 'application',
  BANK_TRANSFER: 'bankTransfer',
});

export const ALL_PAYMENT_METHODS = Object.freeze(Object.values(PAYMENT_METHODS));


// ── Check Statuses ──────────────────────────────────────────
export const CHECK_STATUSES = Object.freeze({
  OPEN: 'open',
  CLOSED: 'closed',
  PARTIALLY_PAID: 'partiallyPaid',
});

export const ALL_CHECK_STATUSES = Object.freeze(Object.values(CHECK_STATUSES));

// ── Custom Item Pricing ─────────────────────────────────────
// How the finalPrice for a manually-added custom OrderItem was computed.
export const CUSTOM_PRICE_TYPES = Object.freeze({
  FIXED: 'fixed',
  BY_WEIGHT: 'byWeight',
});

export const ALL_CUSTOM_PRICE_TYPES = Object.freeze(Object.values(CUSTOM_PRICE_TYPES));

// Weight unit used when CUSTOM_PRICE_TYPES.BY_WEIGHT.
export const CUSTOM_WEIGHT_UNITS = Object.freeze({
  CARAT: 'carat',
  GRAM: 'gram',
});

export const ALL_CUSTOM_WEIGHT_UNITS = Object.freeze(Object.values(CUSTOM_WEIGHT_UNITS));

// ── Diamond grade short labels (Cut / Polish / Symmetry) ─────
export const DIAMOND_GRADE_ABBREV = Object.freeze({
  Ideal: 'ID',
  Excellent: 'EX',
  'Very Good': 'VG',
  Good: 'G',
  Fair: 'F',
  Poor: 'P',
});

/** Returns short label for grade (e.g. "Very Good" -> "VG"), or value if no mapping */
export function formatGradeShort(value) {
  if (value == null || value === '') return value;
  return DIAMOND_GRADE_ABBREV[value] ?? value;
}
